import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  HostListener,
  OnInit,
  Input,
  Output,
  EventEmitter,
  inject,
  signal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthService } from '../../services/auth/auth.service';
import { SignOutService } from '../../services/sign-out-state/sign-out-state.service';
import { Router, RouterLink } from '@angular/router';
import { ContextHolder } from '@frontegg/angular';
import { Bind } from 'primeng/bind';
import { Button } from 'primeng/button';
import { Tooltip } from 'primeng/tooltip';
import { NgClass } from '@angular/common';
import { Dialog } from 'primeng/dialog';
import { TranslatePipe } from '@ngx-translate/core';

type BannerMenu = 'appSwitcher' | 'settings';

@Component({
  selector: 'app-banner',
  templateUrl: './banner.component.html',
  styleUrl: './banner.component.scss',
  imports: [RouterLink, Bind, Button, Tooltip, NgClass, Dialog, TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BannerComponent implements OnInit {
  private signOutService = inject(SignOutService);
  private authService = inject(AuthService);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);

  @Input() user: any = null;
  @Input() isAccessITPAdmin!: boolean;
  @Input() title: string = 'webCapture';
  @Input() isWebApps: boolean = true;
  @Input() isOutlook: boolean = false;
  @Input() isValidUser: boolean = true;
  @Input() hasWebCapture: boolean = true;
  @Input() hasTranscriptProcessing: boolean = false;
  @Input() isVirtualPrinterOnly: boolean = false;
  @Output() changeContentUrlEvent = new EventEmitter<void>();

  openMenu = signal<BannerMenu | null>(null);
  showAboutModal: boolean = false;

  /**
   * Checks if the current route is for the printer component.
   */
  isPrinter(): boolean {
    return this.router.url.includes('/printer');
  }

  /**
   * Initializes the component and subscribes to the signOutService to listen for sign-out events.
   */
  ngOnInit(): void {
    this.signOutService.signOut$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(signOut => {
      if (signOut && !this.signOutService.isFilingContent) {
        this.signOutService.signOut();
      }
    });
  }

  /**
   * Opens/Closes the selected menu
   * @param menu - The menu to toggle.
   */
  toggleMenu(menu: BannerMenu): void {
    this.openMenu.update(current => (current === menu ? null : menu));
  }

  /**
   * Closes the open menu when focus leaves it.
   * @param event - The focus event that triggered this check.
   */
  onMenuFocusOut(event: FocusEvent): void {
    const menu = event.currentTarget as HTMLElement | null;
    if (!menu?.contains(event.relatedTarget as Node | null)) {
      this.openMenu.set(null);
    }
  }

  /**
   * Closes the open menu when the Escape key is pressed.
   * @param event
   */
  onMenuKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.openMenu.set(null);
      event.preventDefault();
    }
  }

  /**
   * Removes the tenant ID and vanity name from local storage and reloads the page to reset the Content URL.
   * Also emits the changeContentUrlEvent for components that need to handle this differently.
   */
  changeContentUrl(): void {
    // Emit the event for outlook, gmail, and printer
    this.changeContentUrlEvent.emit();

    if (this.isWebApps) {
      localStorage.removeItem('tenantId');
      localStorage.removeItem('vanityName');
      if (!this.isValidUser) {
        this.router.navigate(['/webcapture']);
      } else {
        location.reload();
      }
    }
  }

  /**
   * Signs out the user of the application and redirects them back to the log in page
   */
  signOut(): void {
    if (!this.isValidUser) {
      const baseUrl = ContextHolder.getContext().baseUrl;
      const redirectUri = `${window.location.origin}/webcapture`;
      window.location.href = `${baseUrl}/oauth/logout?post_logout_redirect_uri=${redirectUri}`;
    } else {
      this.signOutService.setSignOutState(true);
    }
  }

  /**
   * Closes the open menu on any click outside the menu toggle buttons.
   * @param event - The document click event.
   */
  @HostListener('document:click', ['$event'])
  public onClick(event: MouseEvent): void {
    const target = event.target as Element | null;
    if (!target?.closest('#app-switcher-button, #settings-button')) {
      this.openMenu.set(null);
    }
  }
}
